import { useState } from "react";
import { toast } from "sonner";
import type { Appointment } from "@shyne/shared/api";
import { activeOrganization, type AppointmentJoin } from "@/repositories/appointments.repository";
import { withAnswerKeepingListedMoney } from "@/repositories/appointment-records";
import { appointmentFieldActions, reportFieldActionRefusal } from "@/services/appointment-field-actions.service";

interface FieldStaffRowActionsProps {
  appointment: AppointmentJoin;
  onAppointmentUpdate?: (updatedAppointment: AppointmentJoin) => void;
  onRefetch?: () => void | Promise<void>;
}

export function useFieldStaffRowActions({ appointment, onAppointmentUpdate, onRefetch }: FieldStaffRowActionsProps) {
  const [accepting, setAccepting] = useState(false);
  const [handingBack, setHandingBack] = useState(false);

  const refetch = async () => {
    await onRefetch?.();
  };

  const showAnswer = async (answer: Appointment | null) => {
    if (answer && onAppointmentUpdate) onAppointmentUpdate(withAnswerKeepingListedMoney(appointment, answer));
    else await refetch();
  };

  const accept = async () => {
    if (accepting) return;
    setAccepting(true);
    try {
      await showAnswer(await appointmentFieldActions.accept(appointment.id));
    } catch (error) {
      await reportFieldActionRefusal(error, "Failed to accept appointment", refetch);
    } finally {
      setAccepting(false);
    }
  };

  const handBack = async (reason: string, onSent: () => void) => {
    if (handingBack) return;
    setHandingBack(true);
    try {
      const handedBack = await appointmentFieldActions.handBack(appointment.id, reason, activeOrganization());
      toast.success("Reassignment request sent successfully");
      onSent();
      await showAnswer(handedBack);
    } catch (error) {
      await reportFieldActionRefusal(error, "Failed to send reassignment request", refetch);
    } finally {
      setHandingBack(false);
    }
  };

  return { accept, handBack, handingBack };
}
